import {
  DEFAULT_NUMBER_OF_SEGMENTS_FOR_BAR_CHARTS,
  DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS,
  MAXIMUM_NUMBER_OF_SEGMENTS_FOR_BAR_CHARTS,
  MAXIMUM_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS,
} from '../consts';
import {
  LEGEND_AVAILABLE_WIDTH,
  LEGEND_SWATCH_SIZE,
  MAX_LEGEND_NAME_LENGTH_BEFORE_OVERFLOW,
  MAX_LEGEND_WIDTH_DIVIDER,
} from './common-chart-config';

import {
  APIBarChartAttributes,
  APIPieChartAttributes,
  DrilldownMappings,
  FilterValue,
  NumberWidgetTrend,
  NumberWidgetTrendRangeType,
  RollingDateRange,
  WidgetTypes,
} from '@ardoq/api-types';
import { WidgetLinkedDashboardFilter } from './types';
import { isEmpty } from 'lodash';
import {
  UsageMetricsModeWidgetProps,
  ViewModeChartWidgetProps,
  ViewModeWidgetPropsArrayWithType,
} from './widgetPropTypes';
import {
  ViewModeHeaderWidgetProps,
  ViewModePowerBiIframeWidgetProps,
  ViewModeNoteWidgetProps,
} from './renderTypes';

export type LegendTooltipFormatterParams = {
  componentType: 'legend';
  legendIndex: number;
  name: string;
  $vars: ['name'];
};

export const getDefaultNumberSlicesForWidgetType = (widgetType: WidgetTypes) =>
  widgetType === WidgetTypes.PIE_CHART ||
  widgetType === WidgetTypes.STACKED_BAR_CHART
    ? DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS
    : DEFAULT_NUMBER_OF_SEGMENTS_FOR_BAR_CHARTS;

export const getMaximumNumberOfSlicesForWidgetType = (
  widgetType: WidgetTypes
) =>
  widgetType === WidgetTypes.PIE_CHART ||
  widgetType === WidgetTypes.STACKED_BAR_CHART
    ? MAXIMUM_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS
    : MAXIMUM_NUMBER_OF_SEGMENTS_FOR_BAR_CHARTS;

export const getRecommendedMaxNumberOfSlicesForWidgetType = (
  widgetType: WidgetTypes
) => (widgetType === WidgetTypes.PIE_CHART ? 7 : 20);

const getItemWidthMultiplier = (widgetWidth: number) =>
  widgetWidth < 3 ? 1 : 1.5;

export const getLegendItemWidth = (
  dataLength: number,
  widgetWidth: number
): number => {
  const itemWidthMultiplier = getItemWidthMultiplier(widgetWidth);
  // best balance between size and readability
  return (
    (LEGEND_AVAILABLE_WIDTH * itemWidthMultiplier) /
      (dataLength < MAX_LEGEND_WIDTH_DIVIDER
        ? dataLength
        : MAX_LEGEND_WIDTH_DIVIDER) -
    LEGEND_SWATCH_SIZE
  );
};

export const legendTooltipFormatter = (
  params: LegendTooltipFormatterParams,
  widgetWidth: number
) => {
  const itemWidthMultiplier = getItemWidthMultiplier(widgetWidth);
  return params.name.length >
    MAX_LEGEND_NAME_LENGTH_BEFORE_OVERFLOW * itemWidthMultiplier
    ? params.name
    : (undefined as unknown as string); // tooltip doesn't show with undefined
};

// if the widget is a pie/bar chart, we need to get the id of the data item for user/'parent' columns for drilldown
export const maybeGetPieOrBarChartDataId = (
  name: string,
  chartData: APIPieChartAttributes['data'] | APIBarChartAttributes['data']
) => {
  const dataItem = chartData?.find(d => d.label === name);

  return dataItem && dataItem.id ? dataItem.id : name;
};

export const maybeGetDrilldownMappedValue = (
  name: string,
  drilldownMappings: DrilldownMappings
): FilterValue => (name in drilldownMappings ? drilldownMappings[name] : name);

export const isRollingTrendRange = (
  trend: NumberWidgetTrend
): trend is NumberWidgetTrend & RollingDateRange =>
  trend.rangeType === NumberWidgetTrendRangeType.ROLLING;

export const isActiveLinkedDashboardFilter = (
  linkedDashboardFilter: WidgetLinkedDashboardFilter
): boolean => !isEmpty(linkedDashboardFilter.values);

export const isHeaderProps = (
  props: ViewModeWidgetPropsArrayWithType[number]
): props is ViewModeHeaderWidgetProps & { type: 'header' } =>
  props.type === 'header';

export const isNoteProps = (
  props: ViewModeWidgetPropsArrayWithType[number]
): props is ViewModeNoteWidgetProps & { type: 'note' } => props.type === 'note';

export const isPowerBiIframeProps = (
  props: ViewModeWidgetPropsArrayWithType[number]
): props is ViewModePowerBiIframeWidgetProps & { type: 'powerBiIframe' } =>
  props.type === 'powerBiIframe';

export const isChartProps = (
  props: ViewModeWidgetPropsArrayWithType[number]
): props is (ViewModeChartWidgetProps | UsageMetricsModeWidgetProps) & {
  type: 'chart';
} => props.type === 'chart';
